'use client'

import React from 'react'
import { motion } from 'framer-motion'
import type { RenderedService } from '@/types/interfaces/service-item'
import { textFadeInVariantsNoTransition } from '@/lib/animations'
import Card from './Card'
import { isCategoryDirectoryBackgroundKey, type CategoryDirectoryBackgroundKey } from './Background'
import classes from './CategoryDirectory.module.css'

const DEFAULT_TITLE = 'Shop all categories'

export interface CategoryDirectoryCardData {
    title: string
    href: string
    background: CategoryDirectoryBackgroundKey
}

export interface CategoryDirectoryProps {
    categoryDirectory: RenderedService
}

export default function CategoryDirectory({ categoryDirectory }: CategoryDirectoryProps) {
    const title = categoryDirectory.title?.trim() || DEFAULT_TITLE
    const cards = ((categoryDirectory as { cards?: CategoryDirectoryCardData[] }).cards ?? []).filter(
        (card) =>
            card?.title &&
            card?.href &&
            typeof card.background === 'string' &&
            isCategoryDirectoryBackgroundKey(card.background),
    )

    if (cards.length === 0) return null

    return (
        <motion.section
            className={classes.root}
            data-category-directory={categoryDirectory.id}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px 0px' }}
            variants={textFadeInVariantsNoTransition}
        >
            <h2 className={classes.heading}>{title}</h2>
            <div className={classes.scroller} aria-label={title}>
                {cards.map((card, index) => (
                    <Card
                        key={`${card.href}-${card.title}-${index}`}
                        title={card.title}
                        href={card.href}
                        background={card.background}
                    />
                ))}
            </div>
        </motion.section>
    )
}
